import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/lib/auth/AuthContext';
import { clientApi } from '@/config/axios';

const PERMISSIONS_QUERY_KEY = 'my-permissions';
// Keep permission callbacks stable while the first request is loading. Header
// effects depend on these callbacks and must not re-publish on every render.
const EMPTY_PERMISSIONS: string[] = [];

export function usePermissions() {
  const { user, organization } = useAuth();

  const { data: permissions = EMPTY_PERMISSIONS } = useQuery<string[]>({
    queryKey: [PERMISSIONS_QUERY_KEY, organization?.organization_id],
    queryFn: async () => {
      const res = await clientApi.get('/api/v1/auth/me/permissions', {
        params: { org_id: organization!.organization_id },
      });
      return res.data;
    },
    enabled: !!user && !!organization?.organization_id,
    staleTime: 5 * 60 * 1000,
  });

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  /**
   * True when the current user should bypass per-permission checks in the
   * current org context. Union of two distinct privileges:
   *   - `user.is_superuser` — system-wide override across all orgs
   *   - `organization.is_owner` — org-scoped override for the active org
   *
   * NOTE: `isAdmin` does NOT mean "system superuser". Callers that need to
   * gate on system-superuser only (e.g., the Templates page guard) should
   * read `user.is_superuser` directly. Callers that need org ownership
   * specifically should read `organization?.is_owner`.
   */
  const isAdmin = !!user?.is_superuser || !!organization?.is_owner;

  const can = useCallback((permission: string) => isAdmin || permissionSet.has(permission), [isAdmin, permissionSet]);

  const canAny = useCallback(
    (...perms: string[]) => isAdmin || perms.some((p) => permissionSet.has(p)),
    [isAdmin, permissionSet],
  );

  const canAll = useCallback(
    (...perms: string[]) => isAdmin || perms.every((p) => permissionSet.has(p)),
    [isAdmin, permissionSet],
  );

  return { permissions, can, canAny, canAll, isAdmin };
}
